import TextMarquee from '@/components/common/TextMarquee';
import { commonClickExtend } from '@/tracks/useClickActionT2';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';
import { ProductCardProps } from '../../types';
import { CroppedImage } from '@weee-fe/module';
import { relativeLinkIfQAEnv } from '@/components/component-pool/tools/cms-link';
import { isSayWeeeApp } from '@/utils/weeeapp';

/**
 * 在App中，点击Tag跳转h5页面，新打开webview全屏页面url白名单
 * 在此白名单内的页面url上需要加上fullscreen参数，其他页面无需加
 */
const FULL_SCREEN_PAGE_WHITE_LIST = ['/promotion/top-x'];

/**
 * 此组件为点击可跳转Tag组件
 * 通常展示在商品卡片最底部
 * 目前已知点击跳转页面：Top-x和promotion落地页, promotion不是全屏
 */
const CardBodyLinkedTag: React.FC<Partial<ProductCardProps & { contentClassName?: string }>> = props => {
  const { data, positionInfoT2, cardBodyLinkedTagCls, contentClassName } = props;
  const tag = data?.entrance_tag;
  const router = useRouter();
  const isApp = isSayWeeeApp();

  if (!tag) return null;

  const canIUseFullscreen = (pathname: string) => {
    let result = false;
    for (const item of FULL_SCREEN_PAGE_WHITE_LIST) {
      if (pathname.startsWith(item)) {
        result = true;
        break;
      }
    }
    return result;
  };

  const handleRouterPush = () => {
    if (isApp) {
      const urlObj = new URL(tag?.more_link);
      urlObj.searchParams.set('pushto', 'native');
      const isNeedFullscreen = canIUseFullscreen(urlObj.pathname);
      if (isNeedFullscreen) {
        if (!urlObj.searchParams.has('fullscreen')) {
          urlObj.searchParams.set('fullscreen', '1');
        }
      }
      window.location.href = urlObj.toString();
    } else {
      router.push(relativeLinkIfQAEnv(tag.more_link));
    }
  };

  return (
    <div
      data-testid={`wid-product-card-tag-link-${tag?.tag_type}-${tag?.tag_key}`}
      className={twMerge(
        'inline-flex max-w-full items-center mb-100 box-border px-100 rounded-200 enki-body-3xs-strong !uppercase overflow-hidden gap-1',
        !!tag?.tag_icon_url && 'pl-100',
        !!tag?.tail_icon_url && 'pr-0',
        cardBodyLinkedTagCls
      )}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        commonClickExtend({
          mod_nm: positionInfoT2?.modSecPos.mod_nm ?? 'item_list',
          mod_pos: positionInfoT2?.modSecPos.mod_pos ?? 2,
          sec_nm: null,
          sec_pos: null,
          co: {
            target_nm: data.id,
            target_pos: positionInfoT2?.prodPos,
            target_type: 'chart'
          },
          ctx: { volume_price_support: data?.volume_price_support ? true : false, ...props?.positionInfoT2?.context }
        });
        !!tag?.more_link && handleRouterPush();
      }}
      style={{ backgroundColor: tag?.tag_color, color: tag?.tag_font_color }}>
      {!!tag?.tag_icon_url && (
        <CroppedImage
          alt={'ranking-icon'}
          aria-hidden="true"
          wrapperClassName="shrink-0 w-auto flex items-center"
          width={14}
          height={14}
          url={tag?.tag_icon_url}
          showErrorPlaceholder={false}
          showLoadingPlaceholder={false}
        />
      )}
      {['mkpl_top_ranking', 'top_ranking', 'promotion_event'].includes(tag?.tag_type) ||
      tag?.tag_key === 'buy_again' ? (
        <div className="w-full overflow-hidden !leading-[14px] !uppercase">
          <TextMarquee text={tag?.tag_name} />
        </div>
      ) : (
        <div
          className={twMerge(
            'w-full line-clamp-1 overflow-hidden flex items-center break-all !leading-[14px] !uppercase',
            contentClassName
          )}>
          <span dangerouslySetInnerHTML={{ __html: tag?.tag_name }} />
        </div>
      )}
      {!!tag?.more_link && !!tag?.tail_icon_url && (
        <CroppedImage
          wrapperClassName="shrink-0"
          wrapperStyle={{ width: '19px', height: '19px' }}
          width={19}
          height={19}
          url={tag?.tail_icon_url}
          alt={'ranking-more-link'}
          aria-label="ranking-more-link"
          showErrorPlaceholder={false}
          showLoadingPlaceholder={false}
        />
      )}
    </div>
  );
};

export default CardBodyLinkedTag;
